import { t } from '@lingui/macro'

import { Span } from '@/@bothrs-ui/web/components/typography/Body'
import { SpecialisationSelection } from '@/modules/shared/components/specialisation-selection/SpecialisationSelection'
import { BookingFlowCenteredContent, BookingFlowContent, BookingFlowLayout } from './BookingFlowLayout'
import { ConfirmationContent } from './ConfirmationContent'
import { ContactContent } from './ContactContent'
import { DisclaimerContent } from './DisclaimerContent'
import { EmergencyContent } from './EmergencyContent'
import { ErrorContent } from './ErrorContent'
import { InfoSelect } from './InfoSelect'
import { NoDigitalBookingContent } from './NoDigitalBookingContent'
import { OtherDepartmentContent } from './OtherDepartmentContent'
import { RecommendedCaregiversContent } from './RecommendedCaregiversContent'
import { SingleSelect } from './SingleSelect'
import { SlotSelectionContent } from './SlotSelectionContent'
import { SpecialisationInfoContent } from './SpecialisationInfoContent'
import { SuccessContent } from './SuccessContent'

import type { FlowState, FlowStep, NavigationRequest } from '../types/booking.type'

// Step renderer component that handles different step types
type StepRendererProps = {
  step: FlowStep
  flowState: FlowState
  onAction: (action: NavigationRequest['action'], additionalData?: Record<string, unknown>) => void
  isLoading: boolean
  isLoadingMore: boolean
}

export const StepRenderer = ({ step, flowState, onAction, isLoading, isLoadingMore }: StepRendererProps) => {
  switch (step.type) {
    case 'specialisation-selection': {
      return (
        <BookingFlowLayout>
          <BookingFlowContent>
            <SpecialisationSelection
              isLoading={isLoading}
              description={t({
                id: 'appointments.book.specialisation.subtitlee',
                comment: 'Book appointment: specialisation subtitle',
                message: 'Voor welke specialisatie wens je een afspraak?',
              })}
              onSelect={(item) => {
                onAction('select-specialisation', {
                  specialisationId: item.id,
                })
              }}
              // onSearchResultsChange={handleSearchResultsChange}
              specialisations={step.data?.availableSpecialisations || []}
              onAutoSelectDoctor={() => {
                console.log('auto select doctor not supported yet')
              }}
            />
          </BookingFlowContent>
        </BookingFlowLayout>
      )
    }

    case 'disclaimer': {
      return <DisclaimerContent isLoading={isLoading} onAction={onAction} />
    }

    case 'triage-question': {
      // Important: key by a unique identifier, not by title.
      // Some triage questions share the same title (e.g. pediatrics),
      // and using the title as React key causes component state (selected option)
      // to leak into the next question.
      const triageKey = flowState.currentTriageNodeId ?? step.data?.id ?? step.data?.title

      if (step.data?.questionType === 'SingleSelect') {
        return (
          <SingleSelect
            key={triageKey}
            options={step.data?.options || []}
            title={step.data?.title}
            description={step.data?.description}
            onNext={(option) => onAction('select-answer', { answer: option })}
            isLoading={isLoading}
          />
        )
      }
      if (step.data?.questionType === 'Info') {
        return (
          <InfoSelect
            key={triageKey}
            content={step.data?.content}
            title={step.data?.title}
            onNext={() =>
              onAction('select-answer', {
                answer: {
                  label: step.data?.title,
                  value: 'acknowledged',
                  next: step.data?.next,
                },
              })
            }
            isLoading={isLoading}
          />
        )
      }

      // fallback to single select
      return (
        <SingleSelect
          key={triageKey}
          options={step.data?.options || []}
          title={step.data?.title}
          description={step.data?.description}
          onNext={(option) => onAction('select-answer', { answer: option })}
          isLoading={isLoading}
        />
      )
    }

    case 'specialisation-info': {
      return <SpecialisationInfoContent isLoading={isLoading} flowState={flowState} onAction={onAction} />
    }

    case 'slot-selection': {
      return (
        <SlotSelectionContent
          step={step}
          flowState={flowState}
          onSlotSelect={onAction}
          onLoadMoreSlots={onAction}
          onFilterUpdate={onAction}
          isLoading={isLoading}
          isLoadingMore={isLoadingMore}
        />
      )
    }

    case 'confirmation': {
      return <ConfirmationContent flowState={flowState} isLoading={isLoading} onAction={onAction} />
    }

    case 'no-digital-booking': {
      return (
        <NoDigitalBookingContent
          flowState={flowState}
          data={step.data}
          isLoading={isLoading}
          description={step.description || step.data?.description}
        />
      )
    }

    case 'other-department': {
      return (
        <OtherDepartmentContent
          flowState={flowState}
          data={step.data}
          isLoading={isLoading}
          title={step.title || step.data?.title}
          description={step.description || step.data?.description}
        />
      )
    }

    case 'contact': {
      console.log('step', step)
      return (
        <ContactContent
          flowState={flowState}
          isLoading={isLoading}
          title={step.title || step.data?.title}
          description={step.description || step.data?.description}
        />
      )
    }

    case 'error': {
      return (
        <ErrorContent
          flowState={flowState}
          isLoading={isLoading}
          onGoBack={step.canGoBack ? () => onAction('previous') : undefined}
        />
      )
    }

    case 'emergency': {
      return (
        <EmergencyContent
          flowState={flowState}
          isLoading={isLoading}
          description={step.description || step.data?.description}
        />
      )
    }

    case 'success': {
      return <SuccessContent flowState={flowState} isLoading={isLoading} />
    }

    case 'recommended-caregivers': {
      return <RecommendedCaregiversContent isLoading={isLoading} flowState={flowState} onAction={onAction} />
    }

    default: {
      return (
        <BookingFlowLayout>
          <BookingFlowCenteredContent>
            <Span type='BodyMediumRegular'>Unknown step type: {step.type}</Span>
          </BookingFlowCenteredContent>
        </BookingFlowLayout>
      )
    }
  }
}
